import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { flexRender } from '@tanstack/react-table';
import { Icon } from '../../../icon/src';
import { Text } from '../../../typography/src';
/**
 * TableHead was being written twice because of the sticky vs non-sticky versions of it.
 * Extracted it here instead and using it inside the render function.
 */
export const TableHead = (_a) => {
    var { ariaHidden } = _a, rowProps = __rest(_a, ["ariaHidden"]);
    return (_jsx("thead", { "aria-hidden": ariaHidden, className: 'nucleus-box-border nucleus-w-full nucleus-border-solid nucleus-bg-white nucleus-border-0 nucleus-border-current', children: _jsx(TableHeadRow, Object.assign({}, rowProps)) }));
};
/**
 * The header `<tr>` (with its `<th>` cells) on its own, so it can be reused both inside `TableHead` (wrapped in a
 * `<thead>`) and as `fixedHeaderContent` for the virtualized table, where react-virtuoso provides the `<thead>`.
 */
export const TableHeadRow = ({ isSelectable, roundedBorder, flatHeaders, enableSorting, enableExpandableRows = false, autoColumnWidth = false, }) => {
    // Precompute left-pin offsets: for each left-pinned header, its sticky left value in px.
    const leftPinOffsets = (() => {
        const pinned = flatHeaders
            .filter((h) => h.column.getIsPinned() === 'left')
            .sort((a, b) => a.column.getPinnedIndex() - b.column.getPinnedIndex());
        const offsets = new Map();
        let acc = 0;
        pinned.forEach((h) => {
            offsets.set(h.id, acc);
            acc += h.getSize();
        });
        return offsets;
    })();
    return (_jsx(_Fragment, { children: _jsxs("tr", { className: 'nucleus-box-border nucleus-border-solid nucleus-bg-white nucleus-border-0 nucleus-border-current', children: [enableExpandableRows && (_jsx("th", { className: `nucleus-box-border nucleus-w-0 nucleus-border-solid nucleus-bg-neutral-50 nucleus-border-0 nucleus-border-b-1 nucleus-border-neutral-300
                nucleus-px-4 ${enableSorting ? 'nucleus-py-3' : 'nucleus-py-4'}
                `, "aria-label": 'Expand row' })), isSelectable && (_jsx("th", { className: `nucleus-box-border nucleus-w-0 nucleus-border-solid nucleus-bg-neutral-50 nucleus-border-0 nucleus-border-b-1 nucleus-border-neutral-300
                nucleus-px-4 ${enableSorting ? 'nucleus-py-3' : 'nucleus-py-4'}
                ` })), flatHeaders.map((header, index) => {
                    var _a;
                    const pinned = header.column.getIsPinned();
                    const pinnedStyle = pinned === 'left'
                        ? { position: 'sticky', left: (_a = leftPinOffsets.get(header.id)) !== null && _a !== void 0 ? _a : 0, zIndex: 2 }
                        : undefined;
                    return (_jsx("th", { style: autoColumnWidth
                            ? pinnedStyle
                            : Object.assign({ width: header.getSize(), minWidth: header.column.columnDef.minSize }, pinnedStyle), className: `nucleus-box-border nucleus-border-solid nucleus-bg-neutral-50 nucleus-font-medium nucleus-text-text-secondary nucleus-border-0
            nucleus-border-b-1 nucleus-border-neutral-300 nucleus-px-6
            nucleus-text-left
            ${roundedBorder ? 'first:nucleus-rounded-tl-lg last:nucleus-rounded-tr-lg' : 'nucleus-rounded-none'} ${enableSorting ? 'nucleus-py-3' : 'nucleus-py-4'}`, children: _jsxs("div", { className: `nucleus-group nucleus-flex nucleus-gap-2 nucleus-items-center${header.column.getCanSort() ? ' nucleus-cursor-pointer nucleus-select-none' : ''}`,
                            onClick: header.column.getToggleSortingHandler(), children: [_jsx(Text, { as: 'span', size: 'xs', children: flexRender(header.column.columnDef.header, header.getContext()) }), enableSorting && (_jsx("span", { className: 'nucleus-text-neutral-450 group-hover:nucleus-text-primary-500', children: {
                                        asc: _jsx(Icon, { name: 'sortUp', color: 'current' }),
                                        desc: _jsx(Icon, { name: 'sortDown', color: 'current' }),
                                        notSorted: (
                                        /** Adding an invisible icon so the headers won't change in width
                                         * between showing and hiding the icon
                                         */
                                        _jsx("span", { className: 'nucleus-opacity-0', children: _jsx(Icon, { name: 'sortUp' }) })),
                                    }[header.column.getIsSorted() || 'notSorted'] }))] }) }, `${header.id}-${index}`));
                })] }) }));
};
//# sourceMappingURL=table_head.js.map